import { AppNavigationService } from "@/layout/service/app.navigation.service";
import { AddonBin } from "@/model/entities/addon-bin";
import { AddonBinStationStatus } from "@/model/entities/addon-bin-station-status";
import {
  AddonBinStationStatusEnum,
  AddonBinStatusEnum,
  ItemDetailStatusEnum,
} from "@/model/entities/enums";
import { ItemDetail } from "@/model/entities/item-detail";
import { ItemDetailAddon } from "@/model/entities/item-detail-addon";
import { GridOptionsExt } from "@/shared/ag-fns";
import { AddonUtilsService } from "@/shared/addon-utils.service";
import { DbQueryService } from "@/shared/db-query.service";
import { DbSaveService } from "@/shared/db-save.service";
import { DomainBaseComponent, wrapBusy } from "@/shared/domain-base.component";
import { DomainService } from "@/shared/domain.service";
import { GridReadyEvent } from "@ag-grid-community/core";
import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { takeUntil } from "rxjs";

export enum BinScanStatusEnum {
  binNotSet = 0,
  binNotFound = 1,
  binNotReady = 2,
  binReady = 3,
  binOnHold = 4,
  binComplete = 99,
}

@Component({
  selector: "app-scan",
  templateUrl: "./scan.component.html",
  standalone: false,
})
export class ScanComponent extends DomainBaseComponent implements OnInit {
  @ViewChild("scanInput") scanInput!: ElementRef<HTMLInputElement>;
  @ViewChild("scanShipPrep") scanShipPrep!: ElementRef<HTMLInputElement>;
  @ViewChild("scanBin") scanBin!: ElementRef<HTMLInputElement>;

  binId!: number;
  bin!: AddonBin;
  binItems: ItemDetailAddon[] = [];
  binScanStatus: BinScanStatusEnum;
  scannedItemDetailId: string;
  scanErrorMessage = "";
  scannedShipPrepDetailId: string;
  shipPrepMessage = "";
  itemDetailAddons: ItemDetailAddon[] = [];
  shipPrepItems: ItemDetail[] = [];
  detailGridOptions: GridOptionsExt;
  shipPrepGridOptions: GridOptionsExt;
  isInProcess = true;

  constructor(
    private appNavigation: AppNavigationService,
    private queryService: DbQueryService,
    private saveService: DbSaveService,
    protected route: ActivatedRoute,
    protected domainService: DomainService,
    protected addonUtilsService: AddonUtilsService
  ) {
    super(domainService);

    this.route.queryParamMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });
  }
  row: any;

  async ngOnInit() {
    this.appNavigation.hide();
  }

  async updateFromContext() {
    this.detailGridOptions = this.AgFns.initGridOptions(this, {
      onGridReady: this.onDetailGridReady,
    });

    this.shipPrepGridOptions = this.AgFns.initGridOptions(this, {
      onGridReady: this.onShipPrepGridReady,
    });
  }

  setInProcess(inProcess: boolean) {
    this.isInProcess = inProcess;
    if (inProcess) {
      this.clearBin();
    }
    if (!inProcess) {
      this.shipPrepMessage = "";
      this.shipPrepItems = [];
      this.scanShipPrep.nativeElement.focus();
    }
  }

  async getBin() {
    const g = this.binId;
    this.itemDetailAddons = [];
    this.binScanStatus = BinScanStatusEnum.binNotSet;
    this.bin = await this.queryService.getAddonBin(this.binId);

    this.setBinScanStatus();

    if (this.bin) {
      this.itemDetailAddons =
        await this.queryService.getItemDetailAddonsForAddonBin(this.bin.id);

      this.itemDetailAddons = this.itemDetailAddons.filter(
        (x) =>
          x.addon.addonStationId == this.bin.currentAddonStationId ||
          x.addon.addonStationId == this.bin.pendingAddonStationId
      );

      this.AgFns.refreshGrid(this.detailGridOptions, this.itemDetailAddons);
    }
  }

  onDetailGridReady(event: GridReadyEvent) {
    const colDefs = [
      { headerName: "Status", field: "addonBinStationStatus.name" },
      {
        headerName: "Inventory Identifier",
        field: "itemDetail.id",
        cellClass: "ag-left-aligned-cell",
      },
      {
        headerName: "Description",
        field: "itemDetail.product.productType.description",
      },
    ];

    const sortModel = [{ colId: "itemDetail.id", sort: "asc" as const }];
    this.AgFns.initGrid(event, colDefs, sortModel);
    this.AgFns.autoSizeAllColumns(this.detailGridOptions);
  }

  onShipPrepGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        headerName: "Inventory Identifier",
        field: "id",
        cellClass: "ag-left-aligned-cell",
      },
      {
        headerName: "Description",
        field: "product.productType.description",
      },
    ];

    const sortModel = [{ colId: "id", sort: "asc" as const }];
    this.AgFns.initGrid(event, colDefs, sortModel);
    this.AgFns.autoSizeAllColumns(this.shipPrepGridOptions);
  }

  private async setBinScanStatus() {
    if (!this.binId) {
      this.binScanStatus = BinScanStatusEnum.binNotSet;
    } else if (!this.bin) {
      this.binScanStatus = BinScanStatusEnum.binNotFound;
    } else if (this.bin && !this.bin.currentAddonBinStationStatus) {
      this.binScanStatus = BinScanStatusEnum.binReady;
      await this.UtilFns.wait(1);
      this.scanInput.nativeElement.focus();
    } else if (
      this.bin &&
      this.bin.currentAddonBinStationStatusId ===
        AddonBinStationStatusEnum.Closed
    ) {
      this.binScanStatus = BinScanStatusEnum.binComplete;
    } else if (
      this.bin &&
      (this.bin.currentAddonBinStationStatusId ===
        AddonBinStationStatusEnum.Await ||
        this.bin.currentAddonBinStationStatusId ===
          AddonBinStationStatusEnum.InProcess)
    ) {
      this.binScanStatus = BinScanStatusEnum.binNotReady;
    } else if (
      this.bin &&
      this.bin.currentAddonBinStationStatusId ===
        AddonBinStationStatusEnum.OnHold
    ) {
      this.binScanStatus = BinScanStatusEnum.binOnHold;
    }
  }

  binStatusStr() {
    if (this.binScanStatus === BinScanStatusEnum.binReady) {
      return "Scan into: " + this.bin.pendingAddonStation!.name;
    }

    if (this.binScanStatus === BinScanStatusEnum.binNotFound) {
      return "Bin was not found";
    }

    if (this.binScanStatus === BinScanStatusEnum.binComplete) {
      return "Scan into Ship Prep";
    }

    if (this.binScanStatus === BinScanStatusEnum.binNotReady) {
      return "Bin already scanned into station";
    }

    if (this.binScanStatus === BinScanStatusEnum.binOnHold) {
      return "Bin is on-hold";
    }

    if (this.binScanStatus === BinScanStatusEnum.binNotSet) {
      return "";
    }
  }

  progressStr() {}

  private inBeScannedCt() {}

  okToScan() {
    return this.binScanStatus === BinScanStatusEnum.binReady;
  }

  clearBin() {
    this.binId = null;
    this.bin = null;
    this.setBinScanStatus();
  }

  // onShipPrepInputFilled(event: { target: any }, fn: (value: any) => void) {
  //   const input = event.target;
  //   const length = input.value.length;
  //   const maxLength = input.attributes.maxlength.value;

  //   if (length < maxLength) return;

  //   if (length >= maxLength) {
  //     // bind is needed to associate the fn returned with 'this' component.
  //     fn.bind(this)(input.value);
  //   } else {
  //     this.scanErrorMessage = "";
  //   }

  //   this.getBin();
  // }

  async onShipPrepScan(event: { target: any }, itemDetailId: string) {
    this.shipPrepMessage = "";
    const maxLength = event.target.attributes.maxlength.value;
    const length = event.target.value.length;

    if (length < maxLength) return;

    const itd = await this.queryService.getItemDetailById(itemDetailId);
    if (!itd) {
      this.shipPrepMessage = "* Not found *";
      return;
    }

    if (
      itd?.itemDetailStatusId == ItemDetailStatusEnum.InProcessPendingShipPrep
    ) {
      itd.itemDetailStatusId = ItemDetailStatusEnum.InProcessShipPrep;
      this.shipPrepItems.push(itd);
      await this.dbSaveService.saveChanges();
    } else if (
      itd.itemDetailStatusId == ItemDetailStatusEnum.InProcessShipPrep
    ) {
      this.shipPrepMessage = `* Already scanned *`;
    } else {
      this.shipPrepMessage = `* Not found. `;
    }

    // if (
    //   itd.itemDetailAddons.length == 0 ||
    //   itd.itemDetailStatusId == ItemDetailStatusEnum.InProcessPendingShipPrep
    // ) {
    //   this.shipPrepItems.push(itd);
    //   this.AgFns.refreshGrid(this.shipPrepGridOptions, this.shipPrepItems);
    // }

    this.AgFns.refreshGrid(this.shipPrepGridOptions, this.shipPrepItems);
    this.scannedShipPrepDetailId = "";
    this.scanShipPrep.nativeElement.focus();
  }

  onInputFilled(event: { target: any }, fn: (value: any) => void) {
    const input = event.target;
    const length = input.value.length;
    const maxLength = input.attributes.maxlength.value;

    if (length < maxLength) return;

    if (length >= maxLength) {
      // bind is needed to associate the fn returned with 'this' component.
      fn.bind(this)(input.value);
    } else {
      this.scanErrorMessage = "";
    }

    this.getBin();
  }

  @wrapBusy
  async scanItemDetailId(itemDetailId: string) {
    const itdas = this.addonUtilsService.findScannableAddons(this.bin, this.itemDetailAddons, itemDetailId);

    if (itdas.length == 0) {
      this.scanErrorMessage = "* Invalid scan *";
      this.scannedItemDetailId = "";
      await this.UtilFns.wait(1);
      this.scanInput.nativeElement.focus();
      return;
    } else {
      this.scanErrorMessage = "";
    }

    const isPendingStationAllScanned = this.addonUtilsService.applyScanIntoPendingStation(
      this.bin,
      this.itemDetailAddons,
      itdas
    );

    await this.saveService.saveChanges();
    this.scannedItemDetailId = "";
    if (!isPendingStationAllScanned) {
      await this.UtilFns.wait(1);
      this.scanInput.nativeElement.focus();
    }

    // this.AgFns.refreshGrid(this.detailGridOptions, this.itemDetailAddons);
  }
}
